import styled, { css } from 'styled-components';
import { positions, alignments } from '.';
import {
  mqMediumMin,
  mqMediumMax,
  mqLargeMin,
  mqXXLargeMin,
  mqXXLargeMax,
  mqLargeMax,
} from 'styled/tools/media-queries';
import { TypeBody, TypeHeadingPrimary } from 'components/library/Typography';
import { motion } from 'motion/react';

// Directional mask gradient fading the hero's bottom edge.
// No @seed-health/tokens equivalent — kept local to the only consumer.
const glassEffectBottom = css`
  mask-image: linear-gradient(to top, black 0% 50%, transparent);
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
`;

export const HeroSwiperContainer = styled.section`
  background-color: var(--fill-brand);
  --offset: var(--spacing-48);
  height: min(calc(100vh - 56px), 900px);

  margin-top: 0px;

  @media ${mqLargeMax} {
    margin-top: var(--spacing-8);
  }

  .pause-play-white {
    path {
      stroke: var(--fill-primary);
      color: var(--fill-primary);
      fill: var(--fill-primary);
    }
    margin-right: 6px;
    margin-left: 6px;
  }

  .swiper {
    width: 100%;
    height: 100%;
    overflow: hidden;
    border-radius: 0 0 var(--radius-xl) var(--radius-xl);
  }

  .swiper-slide {
    width: 100%;
    height: 100% !important;
  }

  .swiper-pagination-bullet {
    --swiper-pagination-bullet-inactive-color: var(--fill-primary);
    transition: all 0.25s ease-in-out;
  }

  .swiper-pagination-bullet-active {
    background-color: #fff;
    border-radius: 99em;
    height: 16px;
  }

  .swiper-vertical > .swiper-pagination-bullets,
  .swiper-pagination-vertical.swiper-pagination-bullets {
    bottom: 0;
    right: 50%;
    top: auto;
  }

  &:has(.swiper-pagination) .content {
    @media ${mqMediumMax} {
      padding-block-end: var(--spacing-40);
    }
  }
`;

export const SlideContent = styled.div<{
  alignment: alignments;
  position: positions;
  $mobilePosition?: positions;
  $hideFilter?: boolean;
  $desktopVerticalCenter?: boolean;
}>`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: relative;
  padding-inline: var(--spacing-16);
  padding-block-start: var(--offset, var(--spacing-16));
  padding-block-end: calc(var(--offset) + var(--spacing-32));
  width: 100%;

  @media ${mqMediumMin} {
    padding-block-end: calc(var(--offset) + var(--spacing-40));
  }

  @media ${mqLargeMin} {
    // needs to change
    padding-inline: var(--spacing-56);
    padding-bottom: 0px;
  }

  ${({ position }) =>
    position === 'center' &&
    `
      @media ${mqMediumMax} {
        align-items: flex-end;
      }
    `}

  &:before {
    content: '';
    ${({ $hideFilter }) =>
      !$hideFilter &&
      `
      background: linear-gradient(
        0deg,
        rgba(0, 0, 0, 0.2) 0%,
        rgba(0, 0, 0, 0.2) 100%
      );
    `}
    inset: 0;
    position: absolute;
    height: 100%;
    width: 100%;
    z-index: 2;
  }

  &:after {
    @media ${mqLargeMax} {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
      height: 30%;
      z-index: 2;
      ${({ $hideFilter }) => (!$hideFilter ? glassEffectBottom : '')}
      ${({ $hideFilter }) =>
        !$hideFilter &&
        `
        background: rgba(0, 0, 0, 0.06);
        backdrop-filter: blur(15px);
        -webkit-backdrop-filter: blur(15px);
      `}
    }
  }

  ${({ alignment }) =>
    alignment == 'left' &&
    `
      justify-content: start;
    `}

  ${({ position }) =>
    position == 'top' &&
    `
      align-items: start;
    `}

  ${({ position }) =>
    position == 'bottom' &&
    `
      align-items: end;
    `}

  ${({ $desktopVerticalCenter }) =>
    !$desktopVerticalCenter &&
    `
      @media ${mqLargeMin} {
        padding-block-end: var(--spacing-16);
      }
    `}

  ${({ $mobilePosition }) =>
    $mobilePosition &&
    `
      @media ${mqMediumMax} {
        align-items: ${
          $mobilePosition === 'top'
            ? 'flex-start'
            : $mobilePosition === 'bottom'
              ? 'flex-end'
              : 'center'
        };
      }
    `}
`;

export const SlideText = styled.div<{
  alignment: alignments;
}>`
  ${({ alignment }) =>
    alignment == 'center' &&
    `
        text-align: center;
        margin-inline: auto;
  `}
  color: #fff;
  position: relative;
  max-width: 750px;
  z-index: 5;
`;

export const SlideTextContainer = styled.div<{
  position: positions;
  $mobilePosition?: positions;
  $desktopVerticalCenter?: boolean;
  $titleVariant?: string;
}>`
  @media (${mqXXLargeMin}) {
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    max-width: 1440px;
    ${({ position, $desktopVerticalCenter }) =>
      position === 'center' &&
      `
        ${!$desktopVerticalCenter ? 'margin-top: auto;' : ''}
      `}
  }
  @media (min-width: 1550px) {
    padding-inline: 2em;
  }

  ${({ position, $titleVariant }) =>
    position === 'top' &&
    `
      @media ${mqLargeMax} {
        margin-top: ${$titleVariant === 'catalog' ? '102px' : '227px'};
      }
      margin-top: 0px;
    `}

  ${({ position }) =>
    position === 'midtop' &&
    `
      margin-top: -219px;
    `}

  ${({ $mobilePosition }) =>
    $mobilePosition === 'top' &&
    `
      @media ${mqLargeMax} {
        margin-top: 40px;
      }
    `}

  ${({ $mobilePosition }) =>
    $mobilePosition &&
    $mobilePosition !== 'top' &&
    `
      @media ${mqLargeMax} {
        margin-top: 0;
      }
    `}
`;

export const SlideImage = styled(motion.img)<{
  $mobileImageFit?: 'cover' | 'contain';
  $mobileBackgroundColor?: string;
  $desktopImageFit?: 'cover' | 'contain';
  $desktopBackgroundColor?: string;
}>`
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;

  @media ${mqLargeMax} {
    ${({ $mobileImageFit, $mobileBackgroundColor }) =>
      $mobileImageFit === 'contain' &&
      `
      object-fit: contain;
      object-position: center center;
      background-color: ${$mobileBackgroundColor || 'transparent'};
    `}
  }

  @media ${mqLargeMin} {
    ${({ $desktopImageFit, $desktopBackgroundColor }) =>
      $desktopImageFit === 'contain' &&
      `
      object-fit: contain;
      object-position: center center;
      background-color: ${$desktopBackgroundColor || 'transparent'};
    `}
  }
`;

// Keeps the absolutely positioned SlideImage laid out as if unwrapped
export const SlidePicture = styled.picture`
  display: contents;
`;

export const SlideVideo = styled(motion.div)`
  height: 100%;
  left: 0;
  object-fit: cover;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 1;
`;

export const HeroHead = styled.div<{
  alignment: alignments;
}>`
  & > * + * {
    margin-top: var(--spacing-16);
  }

  ${({ alignment }) =>
    alignment == 'center' &&
    `
        text-align: center;
        p {
          margin-inline: auto;
        }
  `}
`;

export const HeroTitle = styled(TypeHeadingPrimary)<{
  $titleMobileFontSize?: string;
  $desktopMarginTop?: string;
}>`
  margin-top: ${({ $desktopMarginTop }) => $desktopMarginTop || '20px'};
  @media ${mqMediumMax} {
    margin-top: var(--spacing-16);
    margin-bottom: var(--spacing-16);
    ${({ $titleMobileFontSize }) =>
      $titleMobileFontSize && `font-size: ${$titleMobileFontSize};`}
  }

  &[data-pill]:before {
    content: attr(data-pill);
    color: var(--fill-primary);
    font-family: var(--ff);
    font-size: var(--font-size-350);
    font-weight: 500;
    height: 30px;
    text-transform: uppercase;
    border-radius: var(--radius-full);
    border: 1.5px solid currentColor;
    padding-inline: 12px;
    vertical-align: middle;
    display: inline-flex;
    align-items: center;
    margin-right: var(--spacing-8);
    margin-bottom: var(--spacing-8);
  }
`;

export const HeroSubhead = styled(TypeBody)`
  max-width: 426px;
`;

export const HeroActions = styled.div<{
  alignment: alignments;
  position: positions;
  $mobilePosition?: positions;
}>`
  align-items: center;
  display: flex;
  justify-content: center;
  gap: var(--spacing-16);
  margin-top: var(--spacing-24);
  @media ${mqLargeMin} {
    margin-top: 28px;
  }

  ${({ alignment, position }) =>
    alignment == 'center' &&
    position == 'bottom' &&
    `
        @media ${mqMediumMax} {
            flex-direction: column;
        }
  `}

  ${({ alignment }) =>
    alignment == 'left' &&
    `
        justify-content: start;
  `}

  ${({ position }) =>
    position == 'bottom' &&
    `
        @media ${mqMediumMax} {
            margin-bottom: var(--spacing-56);
        }
  `}

  ${({ $mobilePosition }) =>
    $mobilePosition === 'bottom' &&
    `
      @media ${mqMediumMax} {
        margin-bottom: var(--spacing-56);
      }
    `}
`;

export const HeroPauseButtonContainer = styled.div`
  position: absolute;
  bottom: var(--spacing-16);
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  justify-content: flex-end;
  max-width: 1440px;
  margin: 0 auto;
  @media ${mqXXLargeMax} {
    padding-right: var(--spacing-16);
  }
`;

export const HeroPauseButton = styled.div`
  /* No positioning needed - flexbox handles it */
`;
